/**
 * 从对象中选择指定的属性
 * @param obj 原始对象
 * @param keys 要选择的属性
 * @returns 选择后的对象
 */
export function pick<T extends object, K extends keyof T>(
  obj: T,
  keys: K[],
): Pick<T, K> {
  const result = {} as Pick<T, K>;
  keys.forEach(key => {
    if (key in obj) {
      result[key] = obj[key];
    }
  });
  return result;
}

/**
 * 判断是否为对象
 * @param value 值
 * @returns 是否为对象
 */
export function isObject(value: unknown) {
  return value !== null && typeof value === 'object';
}

/**
 * 判断值是否为空
 * 支持以下类型：
 * - null/undefined: 返回 true
 * - 字符串: 长度为0返回 true
 * - 数组: 长度为0返回 true
 * - 对象: 没有可枚举属性返回 true
 * - Map/Set: size为0返回 true
 * - 其他类型: 返回 false
 * @param value 要检查的值
 * @returns 是否为空
 */
export function isEmpty(value: unknown): boolean {
  // null 或 undefined
  if (value === null || value === undefined) {
    return true;
  }

  // 字符串
  if (typeof value === 'string') {
    return value.length === 0;
  }

  // 数组
  if (Array.isArray(value)) {
    return value.length === 0;
  }

  // Map 或 Set
  if (value instanceof Map || value instanceof Set) {
    return value.size === 0;
  }

  // 对象
  if (isObject(value)) {
    return Object.keys(value).length === 0;
  }

  // 其他类型（数字、布尔值、函数等）都不为空
  return false;
}
